import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Stack } from "@mui/material";
import request from "src/utils/request";
import Icon from "src/components/icon";
import notion from "src/assets/icons/notion";
import styled from "styled-components";
import NotionSetupStepper from "src/scenes/integrations/notion-setup/setup-stepper";
import { ToastContext } from "src/components/providers/toast-provider";
import { Skeleton } from "@mui/lab";
import useNotionSetupApi from "./use-setup-api";
import fetchStatus from "../../../utils/api/fetch-status";

const PermissionsIcon = styled(Icon)`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px 12px 24px 12px;
    margin: 32px 0 32px 0;
`;

const H1 = styled.h1`
    font-size: 22px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const NotionSetup = ({ isSettings }) => {
    const [selectedTasksDatabases, setSelectedTasksDatabases] = useState([]);
    const [selectedSprintsDatabases, setSelectedSprintsDatabases] = useState([]);
    const [databasesFullPaths, setDatabasesFullPaths] = useState([]);
    const [loadingPathId, setLoadingPathId] = React.useState([]);
    const [loading, setLoading] = useState(false);

    const [allTaskFields, setAllTaskFields] = React.useState([]);
    const [allSprintFields, setAllSprintFields] = React.useState([]);
    const [allBugTypes, setAllBugTypes] = React.useState([]);

    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);

    const navigate = useNavigate();
    const notionSetupApi = useNotionSetupApi();

    React.useEffect(() => {
        if (notionSetupApi.status === fetchStatus.ERROR) {
            navigate("/integrations/failure/notion");
        }
        if (notionSetupApi.status === fetchStatus.SUCCESS) {
            if (notionSetupApi.data?.databases && notionSetupApi.data.databases.length > 0) {
                const paths = {};
                notionSetupApi.data.databases.forEach((db) => {
                    if (db.fullPath) {
                        paths[db.id] = db.fullPath;
                    }
                });
                setDatabasesFullPaths((prevPaths) => ({ ...prevPaths, ...paths }));
            }
        }
    }, [notionSetupApi.status]);

    const handleSaveSetup = async () => {
        setLoading(true);
        const tasksDbFields = allTaskFields.map((field) => ({
            tableType: "tasks",
            notionId: field.value.id,
            notionName: field.value.name,
            adadotName: field.adadotName,
            databaseId: field.databaseId,
        }));
        const ticketTypeFields = allTaskFields.filter(
            (field) => field.adadotName === "ticket_type"
        );
        const bugTypesReduced = Object.entries(allBugTypes).reduce((acc, [bugTypeKey, bugType]) => {
            const ticketTypeField = ticketTypeFields.find(
                (field) => field.databaseId === bugTypeKey
            );
            if (!ticketTypeField) {
                return acc;
            }
            selectedTasksDatabases.forEach((database) => {
                if (database.id === bugTypeKey) {
                    const field = database.properties[
                        ticketTypeField.value.name
                    ].select?.options.find((opt) => opt.id === bugType.id);
                    if (field) {
                        acc.push({
                            tableType: "tasks",
                            notionIds: [field.id],
                            notionNames: [field.name],
                            adadotName: "bug_types",
                            databaseId: bugTypeKey,
                        });
                    }
                }
            });
            return acc;
        }, []);
        tasksDbFields.push(...bugTypesReduced);
        const sprintsDbFields = Object.values(allSprintFields).map((field) => ({
            tableType: "sprints",
            notionId: field.value.id,
            notionName: field.value.name,
            adadotName: field.adadotName,
            databaseId: selectedSprintsDatabases[0].id,
        }));
        const fields = [...tasksDbFields, ...sprintsDbFields].filter(
            (field) => field.notionId && field.notionName
        );
        const body = {
            fields,
            databases: selectedTasksDatabases.map((taskDb, index) => ({
                tasks: {
                    id: taskDb.id,
                    name: taskDb.title[0]?.plain_text || "Untitled Database",
                },
                sprints: {
                    id: selectedSprintsDatabases[index]?.id || "",
                    name:
                        selectedSprintsDatabases[index]?.title[0]?.plain_text ||
                        "Untitled Database",
                },
            })),
        };
        try {
            await request(`/api/notion/setup`, {
                method: "POST",
                body: JSON.stringify(body),
            });
            setMessage(`Notion setup saved successfully.`);
            setSeverity("success");
            setIsToastTriggered(true);
            setLoading(false);
            if (!isSettings) navigate("/dashboard/work-analytics");
        } catch {
            setLoading(false);
            setMessage(`Failed to save Notion setup. Please try again.`);
            setSeverity("warning");
            setIsToastTriggered(true);
        }
    };

    const getDatabaseFullPath = async (databaseId) => {
        setLoadingPathId((prevState) => [...prevState, databaseId]);
        try {
            const response = await request(`/api/notion/database-full-path?id=${databaseId}`, {
                method: "GET",
            });
            setDatabasesFullPaths((prevPaths) => ({ ...prevPaths, [databaseId]: response.path }));
        } catch (error) {
            console.error("Error fetching the database full path:", error);
        } finally {
            setLoadingPathId((prevState) => prevState.filter((id) => id !== databaseId));
        }
    };

    const checkAndFetchFullPaths = (databases) => {
        databases.forEach((database) => {
            if (!databasesFullPaths[database.id]) {
                getDatabaseFullPath(database.id);
            }
        });
    };

    React.useEffect(() => {
        checkAndFetchFullPaths(selectedTasksDatabases);
        checkAndFetchFullPaths(selectedSprintsDatabases);
    }, [selectedTasksDatabases, selectedSprintsDatabases]);

    return (
        <Box mt={isSettings ? 3 : 7} mb={isSettings ? 0 : 10}>
            <Grid
                container
                spacing={3}
                direction="column"
                alignItems="flex-start"
                justifyContent="center"
            >
                {!isSettings && (
                    <Stack direction="row" alignItems="center">
                        <PermissionsIcon svg={notion} width="100px" height="127" />
                        <Stack direction="column" ml={2}>
                            <H1>Notion Setup</H1>
                            <H2>Setup configuration for your Notion integration</H2>
                        </Stack>
                    </Stack>
                )}
                {!notionSetupApi.data ||
                !notionSetupApi.data.databases ||
                !notionSetupApi.data.databases.length ? (
                    <Skeleton
                        variant="rounded"
                        sx={{ borderRadius: "14px" }}
                        width={isSettings ? 520 : "100%"}
                        height={isSettings ? 220 : 320}
                    />
                ) : (
                    <NotionSetupStepper
                        handleSaveSetup={handleSaveSetup}
                        userDatabases={(notionSetupApi.data && notionSetupApi.data.databases) || []}
                        selectedTasksDatabases={selectedTasksDatabases}
                        setSelectedTasksDatabases={setSelectedTasksDatabases}
                        selectedSprintsDatabases={selectedSprintsDatabases}
                        setSelectedSprintsDatabases={setSelectedSprintsDatabases}
                        loading={loading}
                        databasesFullPaths={databasesFullPaths}
                        setAllTaskFields={setAllTaskFields}
                        allTaskFields={allTaskFields}
                        allBugTypes={allBugTypes}
                        setAllBugTypes={setAllBugTypes}
                        allSprintFields={allSprintFields}
                        setAllSprintFields={setAllSprintFields}
                        loadingPathId={loadingPathId}
                    />
                )}
            </Grid>
        </Box>
    );
};

export default NotionSetup;
